import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { Empresa } from '../models/scope';
import { dispararAcaoSimulada } from '../utils/acaoSimulada';

interface ModalCriarEscopoProps {
  aberto: boolean;
  onFechar: () => void;
}

// Grupo Econômico / Grupo de Empresas são específicos do escopo do Maestro Contábil (seção 9.37 do
// handoff — isolados de propósito do agrupamento homônimo do Workflow SOX). Sem essa tela, os dois
// níveis só existiam via API; este modal cobre a criação de ambos, encadeada.
const ModalCriarEscopo: React.FC<ModalCriarEscopoProps> = ({ aberto, onFechar }) => {
  const { usuarios, recarregar } = useMaestroContext();

  const [nomeGrupoEconomico, setNomeGrupoEconomico] = useState('');
  const [responsavelId, setResponsavelId] = useState(0);
  const [enviandoGrupoEconomico, setEnviandoGrupoEconomico] = useState(false);
  const [grupoEconomicoCriado, setGrupoEconomicoCriado] = useState<{ id: number; nome: string } | null>(null);

  const [empresasDisponiveis, setEmpresasDisponiveis] = useState<Empresa[]>([]);
  const [carregandoEmpresas, setCarregandoEmpresas] = useState(false);
  const [nomeGrupoEmpresas, setNomeGrupoEmpresas] = useState('');
  const [empresaIdsSelecionadas, setEmpresaIdsSelecionadas] = useState<number[]>([]);
  const [enviandoGrupoEmpresas, setEnviandoGrupoEmpresas] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setNomeGrupoEconomico(''); setResponsavelId(0); setGrupoEconomicoCriado(null);
    setNomeGrupoEmpresas(''); setEmpresaIdsSelecionadas([]);
  }, [aberto]);

  useEffect(() => {
    if (!grupoEconomicoCriado) return;
    setCarregandoEmpresas(true);
    maestroService.listarEmpresas(grupoEconomicoCriado.id, [])
      .then(setEmpresasDisponiveis)
      .catch(() => setEmpresasDisponiveis([]))
      .finally(() => setCarregandoEmpresas(false));
  }, [grupoEconomicoCriado]);

  const criarGrupoEconomico = () => {
    if (!nomeGrupoEconomico.trim()) return;
    setEnviandoGrupoEconomico(true);
    maestroService.criarGrupoEconomico({ nome: nomeGrupoEconomico.trim(), responsavelId: responsavelId || undefined })
      .then(g => setGrupoEconomicoCriado({ id: g.id, nome: g.nome }))
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar o grupo econômico.'))
      .finally(() => setEnviandoGrupoEconomico(false));
  };

  const alternarEmpresa = (id: number) =>
    setEmpresaIdsSelecionadas(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);

  const criarGrupoEmpresasEFinalizar = () => {
    if (!grupoEconomicoCriado || !nomeGrupoEmpresas.trim() || empresaIdsSelecionadas.length === 0) return;
    setEnviandoGrupoEmpresas(true);
    maestroService.criarGrupoEmpresas({ grupoEconomicoId: grupoEconomicoCriado.id, nome: nomeGrupoEmpresas.trim(), empresaIds: empresaIdsSelecionadas })
      .then(() => { recarregar(); onFechar(); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar o grupo de empresas.'))
      .finally(() => setEnviandoGrupoEmpresas(false));
  };

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth='sm' fullWidth PaperProps={{ className: 'mc-dialog' }}>
      <DialogTitle>Cadastrar Grupo Econômico e Grupo de Empresas</DialogTitle>
      <DialogContent>
        <div className='mc-form-inline' style={{ marginBottom: grupoEconomicoCriado ? 18 : 0 }}>
          <label>Nome do grupo econômico
            <input type='text' value={nomeGrupoEconomico} onChange={e => setNomeGrupoEconomico(e.target.value)} disabled={!!grupoEconomicoCriado} placeholder='Ex.: Grupo Ainumera' />
          </label>
          <label>Responsável
            <select value={responsavelId} onChange={e => setResponsavelId(Number(e.target.value))} disabled={!!grupoEconomicoCriado}>
              <option value={0}>Sem responsável</option>
              {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          </label>
          {!grupoEconomicoCriado && (
            <button type='button' className='mc-btn mc-btn--primary' disabled={enviandoGrupoEconomico || !nomeGrupoEconomico.trim()} onClick={criarGrupoEconomico}>
              {enviandoGrupoEconomico ? 'Criando...' : 'Criar grupo econômico'}
            </button>
          )}
        </div>

        {grupoEconomicoCriado && (
          <>
            <p className='mc-texto-success'><i className='bi bi-check-circle-fill' /> Grupo econômico "{grupoEconomicoCriado.nome}" criado. Agora crie o primeiro grupo de empresas dentro dele.</p>

            <label className='mc-campo'>Nome do grupo de empresas
              <input type='text' value={nomeGrupoEmpresas} onChange={e => setNomeGrupoEmpresas(e.target.value)} placeholder='Ex.: Empresas Operacionais' />
            </label>

            <p style={{ marginTop: 12, marginBottom: 6, fontSize: 11, fontWeight: 700, color: '#8a8fa3' }}>Empresas do grupo</p>
            {carregandoEmpresas
              ? <p className='mc-texto-neutro'>Carregando empresas cadastradas na plataforma...</p>
              : empresasDisponiveis.length === 0
                ? <p className='mc-texto-danger'>Nenhuma empresa cadastrada na plataforma ainda — cadastre uma empresa antes de continuar.</p>
                : (
                  <div className='mc-wizard-lista' style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {empresasDisponiveis.map(emp => (
                      <label key={emp.id} className='mc-checkbox'>
                        <input type='checkbox' checked={empresaIdsSelecionadas.includes(emp.id)} onChange={() => alternarEmpresa(emp.id)} />
                        {emp.nome}{emp.cnpj ? ` — ${emp.cnpj}` : ''}
                      </label>
                    ))}
                  </div>
                )}
          </>
        )}

        <div className='mc-wizard-acoes'>
          <button type='button' className='mc-btn mc-btn--secondary' onClick={onFechar}>{grupoEconomicoCriado ? 'Fazer depois' : 'Cancelar'}</button>
          {grupoEconomicoCriado && (
            <button
              type='button'
              className='mc-btn mc-btn--primary'
              disabled={enviandoGrupoEmpresas || !nomeGrupoEmpresas.trim() || empresaIdsSelecionadas.length === 0}
              onClick={criarGrupoEmpresasEFinalizar}
            >
              {enviandoGrupoEmpresas ? 'Criando...' : 'Criar grupo de empresas e concluir'}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ModalCriarEscopo;
